import { queryOptions } from "@tanstack/react-query";
import type {
  OrgCapabilities,
  ProjectCapabilities,
} from "@/app/capabilities/types";
import { getCoordinatorApi } from "@/lib/api";
import type { OrgProjectScope } from "@/lib/resource-context";
import { selectOrgCan, selectProjectCan } from "./check-permission";
import { orgCapabilitiesKey, projectCapabilitiesKey } from "./query-keys";

/** Capabilities rarely change; refetch at most twice a minute so revoked access propagates without polling. */
const CAPABILITIES_STALE_TIME_MS = 30_000;

const fetchProjectCapabilities = async (
  scope: OrgProjectScope
): Promise<ProjectCapabilities> => {
  const client = getCoordinatorApi(scope);
  const { data } = await client.get<ProjectCapabilities>("/capabilities");
  return data;
};

export const projectCapabilitiesQueryOptions = (scope: OrgProjectScope) =>
  queryOptions<ProjectCapabilities>({
    queryKey: projectCapabilitiesKey(scope),
    staleTime: CAPABILITIES_STALE_TIME_MS,
    queryFn: () => fetchProjectCapabilities(scope),
  });

/** Project capabilities with a pre-bound, cached permission checker select. */
export const projectPermissionsQueryOptions = (scope: OrgProjectScope) =>
  queryOptions({
    ...projectCapabilitiesQueryOptions(scope),
    select: selectProjectCan,
  });

// Fail-closed sentinel for the org capabilities. The checker keys off `enabled`:
// `enabled: true` with no permissions denies every org check. A successful flag-off
// read instead returns `enabled: false` (allow-all), so RBAC-off behavior is unchanged.
export const ORG_AUTHZ_DENY_ALL: OrgCapabilities = {
  authz: { enabled: true, permissions: [] },
};

/**
 * The org `/capabilities` endpoint is Org-Admin-only today (it gates on
 * `org_metadata_read`). Fail closed on any unsuccessful read: if we can't confirm the
 * caller holds the permission — a 403 ("you hold no org permissions") or a
 * transient/network failure — deny every org check. Showing org UI requires an
 * explicit successful grant; on uncertainty we default to no access. A successful
 * flag-off read still returns `enabled: false`, so RBAC-off behavior is unchanged.
 *
 * TODO(PLT-2150): drop the deny-all once the endpoint is member-readable, so a
 * non-admin holding e.g. `billing_read` can discover it.
 */
const fetchOrgCapabilities = async (
  organizationId: string
): Promise<OrgCapabilities> => {
  const client = getCoordinatorApi({ organizationId });
  try {
    const { data } = await client.get<OrgCapabilities>("/capabilities");
    return data;
  } catch {
    return ORG_AUTHZ_DENY_ALL;
  }
};

export const orgCapabilitiesQueryOptions = (organizationId: string) =>
  queryOptions<OrgCapabilities>({
    queryKey: orgCapabilitiesKey(organizationId),
    staleTime: CAPABILITIES_STALE_TIME_MS,
    queryFn: () => fetchOrgCapabilities(organizationId),
  });

/** Org capabilities with a pre-bound, cached permission checker select. */
export const orgPermissionsQueryOptions = (organizationId: string) =>
  queryOptions({
    ...orgCapabilitiesQueryOptions(organizationId),
    select: selectOrgCan,
  });
